const VISITOR_KEY = "gotocoin:visitor_id";
const SESSION_KEY = "gotocoin:session_id";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function uuid(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** In-memory last-resort ids so identity is NEVER null, even with storage blocked. */
let memVisitorId: string | null = null;
let memSessionId: string | null = null;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${name.replace(/[:]/g, "\\$&")}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function safeGet(store: "local" | "session", key: string): string | null {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    return s.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(store: "local" | "session", key: string, value: string): void {
  try {
    const s = store === "local" ? window.localStorage : window.sessionStorage;
    s.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/**
 * Persistent anonymous id. Resolution order: memory -> localStorage -> cookie -> mint.
 * Always returns a non-empty string in the browser.
 */
export function getVisitorId(): string {
  if (memVisitorId) return memVisitorId;
  if (typeof window === "undefined") return "ssr";

  const id =
    safeGet("local", VISITOR_KEY) ||
    readCookie(VISITOR_KEY) ||
    uuid();

  memVisitorId = id;
  safeSet("local", VISITOR_KEY, id);
  writeCookie(VISITOR_KEY, id);
  return id;
}

/** Per-tab session id. Always returns a non-empty string in the browser. */
export function getSessionId(): string {
  if (memSessionId) return memSessionId;
  if (typeof window === "undefined") return "ssr";

  const id = safeGet("session", SESSION_KEY) || uuid();
  memSessionId = id;
  safeSet("session", SESSION_KEY, id);
  return id;
}

/**
 * Mint both ids synchronously. Called at module-eval time from the app entry so
 * identity exists before any component, hook, or auth listener can fire an event.
 */
export function ensureIdentity(): { visitorId: string; sessionId: string } {
  return { visitorId: getVisitorId(), sessionId: getSessionId() };
}

// Eager, synchronous minting on first import — no hook, no auth, no timing race.
if (typeof window !== "undefined") {
  ensureIdentity();
}
